/**
 * @module app.shared
 */

import { autoinject } from "aurelia-framework";
import { downloadFile, openFileInWindow } from 'app/shared/lib/files';
import { DocumentsService } from 'app/core/services/documents-service';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class DownloadManager {


    ////****************** Fields and Properties **************************************////
    public documentsConfig: IDocumentsConfig = {};

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly service: DocumentsService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
    ) {
    }

    ////****************** Functions ***********************************************////

    public configureService(documentsConfig: IDocumentsConfig):void {
        this.service.configure(documentsConfig);
    }

    public async handleDownloadFile(event: MouseEvent, document: Core.Document, useRollup: boolean): Promise<void> {
        if(event.shiftKey || event.ctrlKey || this.shouldView(document)) {
            const childWindow =
                event.shiftKey ? window.open('', '_blank') : window.open();
            if(childWindow && this.shouldView(document)) {
                this.viewDocument(document, childWindow, useRollup);
            } else {
                await this.downloadDocument(document, useRollup);
            }
            return;
        }
        await this.downloadDocument(document, useRollup);
    }

    public async downloadDocument(document: Core.Document, useRollup: boolean): Promise<void> {
        if(!document || !document.id) { return; }

        try {
            const blob = useRollup ? await this.service.downloadRollupFile(document.id) : await this.service.downloadFile(document.id);
            const fileName = document.file && document.file.name || 'download';

            downloadFile(blob, fileName);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('core:document.$') } )).show();
        }
    }

    public async viewDocument(document: Core.Document, childWindow: Window, useRollup: boolean): Promise<void> {
        if(!document || !document.id) { return; }
        try {
            const blob = useRollup ? await this.service.downloadRollupFile(document.id) : await this.service.downloadFile(document.id);
            openFileInWindow(childWindow, blob);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('core:document.$') } )).show();
            childWindow.close();
        }
    }

    public shouldView(document: Core.Document): boolean {
        const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.txt'];
        const extension = document.mediaType && document.mediaType.fileExtension || '';
        return allowed.indexOf(extension) >= 0;
    }

    ////****************** API Calls ***********************************************////

}
